/*
 * A partner's invitation for a new client, from `?invite=TOKEN` (the
 * accept_url the partner's email links to).
 *
 * Like a referral code it has to survive signup: the email check, the code
 * step, the Google round trip and the org step, where the new organization
 * accepts it. So it is kept in sessionStorage (per tab, gone with the tab)
 * and stripped from the address bar once read. What the invitation said
 * (the partner, the client's name, the invited email) is kept beside it once
 * iam has answered, so the signup form can lock the email.
 */

import { readApiError } from './apiError';

const KEY = 'dodil_signup_client_invite';
/** Longer than any token iam issues. */
const MAX_LEN = 256;

export interface StoredClientInvite {
    token: string;
    email?: string;
    partnerName?: string;
    clientName?: string;
}

export function normaliseInviteToken(raw: string | null | undefined): string | undefined {
    if (typeof raw !== 'string') return undefined;
    const t = raw.trim();
    if (!t || t.length > MAX_LEN || !/^[A-Za-z0-9._~-]+$/.test(t)) return undefined;
    return t;
}

const session = (): Storage | undefined => {
    try { return window.sessionStorage; } catch { return undefined; }
};

/** Call once at start. Returns true when the URL carried a usable invitation. */
export function rememberClientInvite(search: string, storage: Storage | undefined = session()): boolean {
    const token = normaliseInviteToken(new URLSearchParams(search).get('invite'));
    if (!token || !storage) return false;
    const prev = readClientInvite(storage);
    const next: StoredClientInvite = prev?.token === token ? prev : { token };
    try { storage.setItem(KEY, JSON.stringify(next)); } catch { return false; }
    return true;
}

export function readClientInvite(storage: Storage | undefined = session()): StoredClientInvite | undefined {
    if (!storage) return undefined;
    try {
        const v = JSON.parse(storage.getItem(KEY) || 'null');
        const token = normaliseInviteToken(v?.token);
        if (!token) return undefined;
        const str = (x: unknown) => (typeof x === 'string' && x ? x : undefined);
        return { token, email: str(v.email), partnerName: str(v.partnerName), clientName: str(v.clientName) };
    } catch {
        return undefined;
    }
}

/** Keep what iam said about the invitation (for the locked email). */
export function noteClientInvite(details: Omit<StoredClientInvite, 'token'>, storage: Storage | undefined = session()): void {
    const cur = readClientInvite(storage);
    if (!cur || !storage) return;
    try { storage.setItem(KEY, JSON.stringify({ ...cur, ...details })); } catch { /* ignore */ }
}

export function forgetClientInvite(storage: Storage | undefined = session()): void {
    try { storage?.removeItem(KEY); } catch { /* ignore */ }
}

export function stripInviteParam(search: string): string {
    const params = new URLSearchParams(search);
    if (!params.has('invite')) return search;
    params.delete('invite');
    const s = params.toString();
    return s ? `?${s}` : '';
}

/** ClientInvitationStatus, as the gateway's JSON spells it (name or number). */
export type InviteState = 'pending' | 'accepted' | 'replaced' | 'revoked' | 'expired' | 'unknown';

export function inviteState(status: unknown, expiresAt?: unknown, now = Date.now()): InviteState {
    const s = String(status ?? '');
    let state: InviteState = 'unknown';
    if (s === '1' || /PENDING$/.test(s)) state = 'pending';
    else if (s === '2' || /ACCEPTED$/.test(s)) state = 'accepted';
    else if (s === '3' || /REPLACED$/.test(s)) state = 'replaced';
    else if (s === '4' || /REVOKED$/.test(s)) state = 'revoked';
    else if (s === '5' || /EXPIRED$/.test(s)) state = 'expired';
    // expires_at is unix seconds; a pending invitation past it is expired.
    const exp = Number(expiresAt ?? 0);
    if (state === 'pending' && exp > 0 && exp * 1000 < now) state = 'expired';
    return state;
}

/** What a person reads for an invitation that can't be accepted any more. */
export function inviteStateText(state: InviteState, partnerName = 'the partner', signedIn = false): string | undefined {
    switch (state) {
        case 'accepted': return signedIn
            ? 'This invitation has already been accepted. Your organization works with them on Dodil; open the console to carry on.'
            : 'This invitation has already been accepted. Sign in to continue.';
        case 'replaced': return `${partnerName} sent you a newer invitation. Open the link in the latest email.`;
        case 'revoked': return `${partnerName} withdrew this invitation. Ask them to send a new one if you still want to join.`;
        case 'expired': return `This invitation has expired. Ask ${partnerName} to send a new one.`;
    }
    return undefined;
}

/**
 * A refusal from GetClientInvitation / AcceptClientInvitation, in words.
 * iam's codes: PERMISSION_DENIED when the signed-in email isn't the invited
 * one, FAILED_PRECONDITION when the org already works with a partner (or
 * the invitation is no longer open), NOT_FOUND for an unknown token.
 */
export function inviteErrorText(e: unknown, email?: string): string {
    const { code, status, raw, unreachable } = readApiError(e);

    if (code === 7 || status === 403) {
        return email
            ? `This invitation was sent to ${email}. Sign out, then sign in or sign up with that email.`
            : 'This invitation was sent to a different email. Sign out, then sign in or sign up with the email it was sent to.';
    }
    if (code === 9 || (status === 400 && /partner|precondition/i.test(raw))) {
        if (/partner/i.test(raw)) return 'This organization already works with a partner. Choose another organization you hold, or create a new one for this.';
        if (/expire/i.test(raw)) return 'This invitation has expired. Ask the partner to send a new one.';
        if (/own|holder|owner/i.test(raw)) return 'Only the organization\'s account holder can accept for it. Choose an organization you hold.';
        return 'This invitation can\'t be accepted any more. Ask the partner to send a new one.';
    }
    if (code === 5 || status === 404) return 'This invitation link isn\'t valid. Check you opened the whole link from the email, or ask the partner to send a new one.';
    if (code === 16 || status === 401) return 'Your session has ended. Sign in again to accept the invitation.';
    if (code === 8 || status === 429) return 'Too many attempts just now. Wait a moment and try again.';
    if (unreachable) return 'Dodil couldn\'t be reached. Check your connection and try again.';
    if (code === 4 || code === 14 || (status !== undefined && status >= 502 && status <= 504)) return 'Dodil couldn\'t be reached just now. Try again in a moment.';
    if (status === undefined) return 'That didn\'t work. Try again.';
    if (status >= 500) return 'Something went wrong on Dodil\'s side. Try again in a moment.';
    return 'That didn\'t work. Try again.';
}
